import type { GrammarExercise } from '../exercise-types';

// Everyday-level practice exercises, one entry per topic slug. Written in
// E2E's voice: women protagonists, real situations from the students' lives,
// English as a tool of autonomy.
export const everydayExercises: Record<string, GrammarExercise[]> = {
  'both-neither-either': [
    {
      kind: 'multiple-choice',
      prompt: 'Marina speaks ___ English and Spanish — two tools for her international career.',
      options: ['both', 'neither', 'either'],
      correctIndex: 0,
      explanation: 'Both includes the two options mentioned: not only X but also Y.',
    },
    {
      kind: 'multiple-choice',
      prompt: '“I have never presented in English before.” “___ have I — let\'s prepare together!”',
      options: ['Neither', 'Either', 'Both'],
      correctIndex: 0,
      explanation: 'To agree with a negative sentence, we use Neither + auxiliary + subject.',
    },
    {
      kind: 'multiple-choice',
      prompt: 'Paula got two job offers, but she isn\'t interested in ___ of them — she wants to open her own business.',
      options: ['either', 'neither', 'both'],
      correctIndex: 0,
      explanation: 'In negative sentences and questions, we use either.',
    },
    {
      kind: 'fill-blank',
      prompt: '___ of the two candidates was prepared, so Luiza got the position. (= not one and not the other)',
      answers: ['neither'],
      explanation: 'Neither = not one and not the other, and it takes an affirmative singular verb.',
    },
    {
      kind: 'multiple-choice',
      prompt: 'Which sentence is correct?',
      options: [
        'Neither answer is correct.',
        'Neither answer are correct.',
        'Neither answers is correct.',
      ],
      correctIndex: 0,
      explanation: 'Neither is a negative word accompanied by an affirmative singular verb.',
    },
    {
      kind: 'fill-blank',
      prompt: 'I\'m ___ happy and proud of my progress this month!',
      answers: ['both'],
      explanation: 'Both ... and ... connects two things that are true at the same time.',
    },
  ],

  'present-continuous': [
    {
      kind: 'multiple-choice',
      prompt: 'Right now, Luisa ___ for her first presentation in English.',
      options: ['is preparing', 'are preparing', 'prepare'],
      correctIndex: 0,
      explanation: 'Affirmative ongoing actions use TO BE (is, are, am) + verb in the -ing.',
    },
    {
      kind: 'multiple-choice',
      prompt: 'This week, people at the office ___ about mental health — an important conversation.',
      options: ['are talking', 'is talking', 'talking'],
      correctIndex: 0,
      explanation: 'With we, you, and they, the present continuous uses ARE + verb in the -ing.',
    },
    {
      kind: 'fill-blank',
      prompt: 'I ___ (learn) something new every day — and it feels amazing!',
      answers: ['am learning', '\'m learning', '’m learning'],
      explanation: 'With I, we use AM + verb in the -ing: I am learning.',
    },
    {
      kind: 'multiple-choice',
      prompt: 'Julia ___ enough at night, so she\'s changing her routine.',
      options: ['isn’t sleeping', 'aren’t sleeping', 'doesn’t sleeping'],
      correctIndex: 0,
      explanation: 'The negative uses TO BE + NOT + verb in the -ing: is + not → isn’t.',
    },
    {
      kind: 'fill-blank',
      prompt: 'We ___ not waiting for permission — we\'re building our future now.',
      answers: ['are', '\'re', '’re'],
      explanation: 'With we, the negative is are + not + verb in the -ing.',
    },
    {
      kind: 'multiple-choice',
      prompt: 'Which question is correct?',
      options: [
        'Are you taking care of your mental health?',
        'You are taking care of your mental health?',
        'Do you taking care of your mental health?',
      ],
      correctIndex: 0,
      explanation: 'Questions use TO BE + subject + verb in the -ing.',
    },
    {
      kind: 'fill-blank',
      prompt: '___ Isabella feeling ok today?',
      answers: ['is'],
      explanation: 'To ask about she, he, or it, we start the question with IS.',
    },
  ],

  'adding-ing-gerund': [
    {
      kind: 'multiple-choice',
      prompt: 'wait → ___',
      options: ['waiting', 'waitting', 'waiteing'],
      correctIndex: 0,
      explanation: 'The general rule is to just add -ing to the verb.',
    },
    {
      kind: 'multiple-choice',
      prompt: 'close → ___',
      options: ['closing', 'closeing', 'clossing'],
      correctIndex: 0,
      explanation: 'When a verb ends in -e, we drop the e and add -ing.',
    },
    {
      kind: 'fill-blank',
      prompt: 'run → ___',
      answers: ['running'],
      explanation: 'Verbs ending in CVC (consonant, vowel, consonant) double the final consonant.',
    },
    {
      kind: 'multiple-choice',
      prompt: 'lie → ___',
      options: ['lying', 'lieing', 'liing'],
      correctIndex: 0,
      explanation: 'Verbs ending in -ie drop the -ie and add -ying.',
    },
    {
      kind: 'fill-blank',
      prompt: 'stop → ___',
      answers: ['stopping'],
      explanation: 'Stop ends in CVC, so we double the p before adding -ing.',
    },
    {
      kind: 'fill-blank',
      prompt: 'love → ___ (I\'m ___ it!)',
      answers: ['loving'],
      explanation: 'Love ends in -e, so we drop the e: loving.',
    },
    {
      kind: 'multiple-choice',
      prompt: 'Which verb does NOT double its final consonant before -ing?',
      options: ['speak', 'stop', 'hit'],
      correctIndex: 0,
      explanation: 'Speak doesn’t end in CVC, so we just add -ing: speaking.',
    },
    {
      kind: 'matching',
      instruction: 'Match each verb with its -ing form:',
      pairs: [
        { left: 'die', right: 'dying' },
        { left: 'hate', right: 'hating' },
        { left: 'travel', right: 'travelling' },
        { left: 'visit', right: 'visiting' },
      ],
      explanation: 'Each spelling rule depends on how the verb ends: -ie, -e, CVC, or the general rule.',
    },
    {
      kind: 'fill-blank',
      prompt: 'Amanda is ___ (plan) her first solo trip abroad.',
      answers: ['planning'],
      explanation: 'Plan ends in CVC, so we double the n: planning.',
    },
  ],

  'simple-future': [
    {
      kind: 'multiple-choice',
      prompt: 'I believe Fernanda ___ an amazing leader for this team.',
      options: ['will be', 'wills be', 'will is'],
      correctIndex: 0,
      explanation: 'The simple future uses WILL + main verb in the base form, for all subjects.',
    },
    {
      kind: 'fill-blank',
      prompt: 'Don\'t worry — it ___ rain tomorrow, so we can study in the park. (negative)',
      answers: ['won\'t', 'won’t', 'will not'],
      explanation: 'The negative is will + not, and the contraction is won’t.',
    },
    {
      kind: 'multiple-choice',
      prompt: '___ you join our study group next month?',
      options: ['Will', 'Do', 'Are'],
      correctIndex: 0,
      explanation: 'To ask about the future, we invert the subject and the auxiliary WILL.',
    },
    {
      kind: 'fill-blank',
      prompt: 'I predict that our team ___ (win) the pitch competition.',
      answers: ['will win', '\'ll win', '’ll win'],
      explanation: 'We use the simple future to make predictions or guesses about the future.',
    },
    {
      kind: 'multiple-choice',
      prompt: 'Which sentence is correct?',
      options: [
        'She will travel to Lisbon next year.',
        'She will travels to Lisbon next year.',
        'She wills travel to Lisbon next year.',
      ],
      correctIndex: 0,
      explanation: 'After will, the main verb always stays in the base form.',
    },
    {
      kind: 'fill-blank',
      prompt: '“Will you present the project?” “Yes, I ___ — and I\'ll do it brilliantly.”',
      answers: ['will'],
      explanation: 'Short answers repeat the auxiliary: Yes, I will / No, I won’t.',
    },
  ],

  'will-vs-going-to': [
    {
      kind: 'multiple-choice',
      prompt: 'Camila has already bought her tickets. She ___ visit her sister in Salvador.',
      options: ['is going to', 'will', 'won’t'],
      correctIndex: 0,
      explanation: 'GOING TO is for plans and decisions that have already been made.',
    },
    {
      kind: 'multiple-choice',
      prompt: '“This box is so heavy!” “Don\'t worry, I ___ help you!”',
      options: ['will', 'am going to', 'going to'],
      correctIndex: 0,
      explanation: 'WILL is for decisions made in the moment, like offers.',
    },
    {
      kind: 'multiple-choice',
      prompt: 'From now on, I ___ treat myself with more kindness.',
      options: ['will', 'going to', 'won’t'],
      correctIndex: 0,
      explanation: 'Promises use WILL — it doesn’t indicate a pre-planned action.',
    },
    {
      kind: 'fill-blank',
      prompt: 'Renata decided months ago: she ___ going to open her own café.',
      answers: ['is', '\'s', '’s'],
      explanation: 'Going to needs the verb be before it: she is going to.',
    },
    {
      kind: 'multiple-choice',
      prompt: '“Are you busy this evening?” “Yes, I ___ to the movies — I bought the tickets yesterday.”',
      options: ['am going to go', 'will go', 'won’t go'],
      correctIndex: 0,
      explanation: 'A plan made before the question takes GOING TO — we cannot use WILL here.',
    },
    {
      kind: 'fill-blank',
      prompt: 'I haven\'t made plans, so I can say: “I ___ probably watch TV.” (one-word auxiliary)',
      answers: ['will', '\'ll', '’ll'],
      explanation: 'With no previous plan, we are predicting — both WILL and GOING TO are possible.',
    },
  ],

  'adverbs-of-intensity': [
    {
      kind: 'multiple-choice',
      prompt: 'Her presentation is ___ ready — just one slide left.',
      options: ['almost', 'too', 'extremely'],
      correctIndex: 0,
      explanation: 'Almost = nearly, approximately.',
    },
    {
      kind: 'multiple-choice',
      prompt: 'The dress was ___ small for me, so I asked for a bigger size.',
      options: ['too', 'very', 'quite'],
      correctIndex: 0,
      explanation: 'Too = more than it should be — it signals a problem.',
    },
    {
      kind: 'fill-blank',
      prompt: 'Studying English is ___ useful for my career. (more than enough, in a high intensity — starts with pr-)',
      answers: ['pretty'],
      explanation: 'Pretty = more than enough, in a high intensity.',
    },
    {
      kind: 'multiple-choice',
      prompt: 'I have an ___ busy work schedule this month.',
      options: ['extremely', 'almost', 'too'],
      correctIndex: 0,
      explanation: 'Extremely = to a very great intensity.',
    },
    {
      kind: 'fill-blank',
      prompt: 'My sister is ___ smart — she solved the problem in minutes. (in a high intensity, 4 letters)',
      answers: ['very'],
      explanation: 'Very shows a high intensity of the adjective.',
    },
    {
      kind: 'matching',
      instruction: 'Match each adverb of intensity with its meaning:',
      pairs: [
        { left: 'almost', right: 'nearly' },
        { left: 'too', right: 'more than it should be' },
        { left: 'quite', right: 'enough' },
        { left: 'very', right: 'in a high intensity' },
      ],
      explanation: 'Adverbs of intensity show how strong an action, adjective, or adverb is.',
    },
  ],

  'simple-past': [
    {
      kind: 'fill-blank',
      prompt: 'Last month, Carla ___ (negotiate) her salary — and she got the raise!',
      answers: ['negotiated'],
      explanation: 'Negotiate is a regular verb ended in -e, so we just add -d: negotiated.',
    },
    {
      kind: 'multiple-choice',
      prompt: 'Yesterday I ___ to my first meeting in English — and I did great!',
      options: ['goed', 'went', 'go'],
      correctIndex: 1,
      explanation: 'Go is an irregular verb: its simple past is went.',
    },
    {
      kind: 'fill-blank',
      prompt: 'I ___ not give up, even when the job interview got difficult.',
      answers: ['did'],
      explanation: 'In the negative, we use did not (didn’t) + the verb in the infinitive.',
    },
    {
      kind: 'multiple-choice',
      prompt: '___ you watch the movie without subtitles?',
      options: ['Did', 'Do', 'Were'],
      correctIndex: 0,
      explanation: 'Questions in the simple past use Did + subject + verb in the infinitive.',
    },
    {
      kind: 'matching',
      instruction: 'Match each verb with its simple past form:',
      pairs: [
        { left: 'to teach', right: 'taught' },
        { left: 'to speak', right: 'spoke' },
        { left: 'to build', right: 'built' },
      ],
      explanation: 'Irregular verbs don’t follow a pattern — knowing the most common ones makes a big difference.',
    },
    {
      kind: 'multiple-choice',
      prompt: 'Pronunciation point: in which verb does the -ed ending sound like /ɪd/, with a new syllable?',
      options: ['wanted', 'played', 'watched'],
      correctIndex: 0,
      explanation: 'Verbs ended in -t or -d (want, end, visit) get the /ɪd/ sound: wan-ted.',
    },
  ],

  'genitive-case': [
    {
      kind: 'multiple-choice',
      prompt: 'I borrowed ___ calculator for the budget meeting.',
      options: ['Sam’s', 'Sam', 'of Sam'],
      correctIndex: 0,
      explanation: 'To show possession with a singular noun, we add ’s to the possessor.',
    },
    {
      kind: 'multiple-choice',
      prompt: 'The two ___ house is next to mine.',
      options: ['sisters’', 'sister’s', 'sisters'],
      correctIndex: 0,
      explanation: 'Plural nouns ending in -s only take the apostrophe: sisters’.',
    },
    {
      kind: 'fill-blank',
      prompt: 'Tomorrow, we\'re all going to see the ___ (museum) art exhibit.',
      answers: ['museum\'s', 'museum’s'],
      explanation: 'Singular nouns take ’s: the museum’s art exhibit = the art exhibit of the museum.',
    },
    {
      kind: 'multiple-choice',
      prompt: 'Which is correct for “the toys of the children”?',
      options: ['the children’s toys', 'the childrens’ toys', 'the childrens toys'],
      correctIndex: 0,
      explanation: 'Children is a plural that doesn’t end in -s, so it takes ’s like a singular noun.',
    },
    {
      kind: 'fill-blank',
      prompt: 'The ___ (players) boots were dirty after the game.',
      answers: ['players\'', 'players’'],
      explanation: 'Plural nouns ending in -s only add the apostrophe: the players’ boots.',
    },
    {
      kind: 'multiple-choice',
      prompt: 'The house belongs to my mother. → ___ is next to the beach.',
      options: ['My mother’s house', 'My mothers house', 'The house of she'],
      correctIndex: 0,
      explanation: 'The genitive case turns “the house of my mother” into “my mother’s house”.',
    },
  ],

  'english-vowel-sounds': [
    {
      kind: 'multiple-choice',
      prompt: 'Which pair shows that changing the vowel sound changes the meaning?',
      options: ['ship / sheep', 'book / books', 'play / played'],
      correctIndex: 0,
      explanation: 'Ship has the short /ɪ/ and sheep has the long /iː/ — two different words.',
    },
    {
      kind: 'multiple-choice',
      prompt: 'The word “cut” has which kind of vowel sound?',
      options: ['a short vowel /ʌ/', 'a long vowel /ɑː/', 'a diphthong /aɪ/'],
      correctIndex: 0,
      explanation: 'Short vowels are quick, relaxed sounds — cart is the one with the long /ɑː/.',
    },
    {
      kind: 'multiple-choice',
      prompt: 'Which word has the long vowel /iː/?',
      options: ['week', 'fit', 'pet'],
      correctIndex: 0,
      explanation: 'Long vowels are longer and tenser, often similar to the letter name: week /wiːk/.',
    },
    {
      kind: 'matching',
      instruction: 'Match each vowel sound with a word that contains it:',
      pairs: [
        { left: '/ɪ/ (short)', right: 'fit' },
        { left: '/iː/ (long)', right: 'week' },
        { left: '/ʊ/ (short)', right: 'book' },
        { left: '/uː/ (long)', right: 'group' },
      ],
      explanation: 'Short vowels are quick and relaxed; long vowels are longer and tenser.',
    },
    {
      kind: 'multiple-choice',
      prompt: 'Which word contains a diphthong (a double vowel)?',
      options: ['place', 'pet', 'put'],
      correctIndex: 0,
      explanation: 'Place has the diphthong /eɪ/ — a vowel followed by a semivowel.',
    },
    {
      kind: 'fill-blank',
      prompt: 'Full has the short sound /ʊ/; ___ has the long sound /uː/.',
      answers: ['fool'],
      explanation: 'Full /ʊ/ vs fool /uː/ — the vowel length changes the meaning.',
    },
  ],

  'a-or-an': [
    {
      kind: 'multiple-choice',
      prompt: '___ apple a day keeps the doctor away.',
      options: ['An', 'A'],
      correctIndex: 0,
      explanation: 'Apple starts with a vowel sound, so we use AN.',
    },
    {
      kind: 'multiple-choice',
      prompt: 'The meeting lasted ___ hour.',
      options: ['an', 'a'],
      correctIndex: 0,
      explanation: 'The h in hour is silent — the word starts with a vowel sound, so we use AN.',
    },
    {
      kind: 'multiple-choice',
      prompt: 'Beatriz was the first in her family to attend ___ university.',
      options: ['a', 'an'],
      correctIndex: 0,
      explanation: 'University starts with the semivowel sound /j/, so we use A.',
    },
    {
      kind: 'fill-blank',
      prompt: 'Sofia is ___ engineer at a tech company.',
      answers: ['an'],
      explanation: 'Engineer starts with a vowel sound, so we use AN.',
    },
    {
      kind: 'fill-blank',
      prompt: 'She booked ___ one-way ticket to London — her dream trip!',
      answers: ['a'],
      explanation: 'One starts with the semivowel sound /w/, so we use A.',
    },
    {
      kind: 'multiple-choice',
      prompt: 'Ana is presenting at ___ European conference next month.',
      options: ['a', 'an'],
      correctIndex: 0,
      explanation: 'European starts with the semivowel sound /j/, so we use A.',
    },
    {
      kind: 'fill-blank',
      prompt: 'It was ___ honest conversation about salaries.',
      answers: ['an'],
      explanation: 'The h in honest is silent, so the word starts with a vowel sound: AN.',
    },
    {
      kind: 'multiple-choice',
      prompt: 'Why do we say “a user” and not “an user”?',
      options: [
        'User starts with the semivowel sound /j/',
        'User starts with a vowel letter',
        'User is a short word',
      ],
      correctIndex: 0,
      explanation: 'The choice depends on the initial SOUND, not the initial letter.',
    },
  ],

  'talking-about-money': [
    {
      kind: 'multiple-choice',
      prompt: 'A simple way to read $2.50 is:',
      options: ['two-fifty', 'two point fifty dollars', 'two and a half moneys'],
      correctIndex: 0,
      explanation: 'For small prices, we can just say the numbers: two-fifty.',
    },
    {
      kind: 'multiple-choice',
      prompt: '$600.00 → ___',
      options: ['six hundred dollars', 'six zero zero dollars', 'six hundreds dollar'],
      correctIndex: 0,
      explanation: 'If the price is an exact number of dollars, we say it completely.',
    },
    {
      kind: 'fill-blank',
      prompt: '£100 → ___ hundred pounds (one word, instead of “one”)',
      answers: ['a'],
      explanation: 'When the price starts with one, we can use A instead: a hundred pounds.',
    },
    {
      kind: 'multiple-choice',
      prompt: '$1200 can be read as “twelve hundred” or as:',
      options: ['a thousand two hundred', 'one-two-zero-zero dollars', 'twelve thousand'],
      correctIndex: 0,
      explanation: 'When the last two digits are zeros, we can say the price in two ways.',
    },
    {
      kind: 'fill-blank',
      prompt: 'Her startup raised $1,000,000 — a ___ dollars!',
      answers: ['million'],
      explanation: '$1,000,000 → a million dollars.',
    },
    {
      kind: 'multiple-choice',
      prompt: 'R$ 8800 → ___',
      options: ['eighty eight hundred reais', 'eight hundred eighty reais', 'eighty-eight thousand reais'],
      correctIndex: 0,
      explanation: 'With two final zeros, we can count in hundreds: eighty eight hundred reais.',
    },
  ],

  'irregular-plural-nouns': [
    {
      kind: 'multiple-choice',
      prompt: 'Rafaela presented three ___ in her report — her boss was impressed.',
      options: ['analyses', 'analysis', 'analysises'],
      correctIndex: 0,
      explanation: 'Nouns ending in -is change to -es: analysis → analyses.',
    },
    {
      kind: 'multiple-choice',
      prompt: 'What is the plural of woman?',
      options: ['women', 'womans', 'womens'],
      correctIndex: 0,
      explanation: 'Woman is irregular: the vowel changes, and so does the pronunciation.',
    },
    {
      kind: 'fill-blank',
      prompt: 'One child, two ___.',
      answers: ['children'],
      explanation: 'Child is irregular: it takes a different ending, children.',
    },
    {
      kind: 'fill-blank',
      prompt: 'They say cats have nine ___ (life).',
      answers: ['lives'],
      explanation: 'Nouns ending in -fe change the f to v and add -s: life → lives.',
    },
    {
      kind: 'matching',
      instruction: 'Match each noun with its irregular plural:',
      pairs: [
        { left: 'knife', right: 'knives' },
        { left: 'crisis', right: 'crises' },
        { left: 'foot', right: 'feet' },
        { left: 'person', right: 'people' },
        { left: 'phenomenon', right: 'phenomena' },
      ],
      explanation: 'Irregular plurals don’t follow the regular -s rule — learn the most common ones.',
    },
    {
      kind: 'multiple-choice',
      prompt: 'Which plural is correct?',
      options: ['potatoes', 'potatos', 'potatoies'],
      correctIndex: 0,
      explanation: 'Nouns ending in -o add -es: potato → potatoes.',
    },
  ],

  'countable-uncountable-nouns': [
    {
      kind: 'multiple-choice',
      prompt: 'Can I give you ___ advice about the job interview?',
      options: ['some', 'an', 'a few'],
      correctIndex: 0,
      explanation: 'Advice is uncountable — it has no plural and never takes a or an.',
    },
    {
      kind: 'multiple-choice',
      prompt: 'Which noun is uncountable?',
      options: ['furniture', 'chair', 'table'],
      correctIndex: 0,
      explanation: 'Furniture looks countable but isn’t — it only exists in one form.',
    },
    {
      kind: 'fill-blank',
      prompt: 'This ___ (be) great news!',
      answers: ['is'],
      explanation: 'Uncountable nouns take the verb in the singular form.',
    },
    {
      kind: 'multiple-choice',
      prompt: 'Which sentence is correct?',
      options: [
        'She gave me a piece of advice.',
        'She gave me an advice.',
        'She gave me advices.',
      ],
      correctIndex: 0,
      explanation: 'Advice is uncountable, so we count it with “a piece of”.',
    },
    {
      kind: 'fill-blank',
      prompt: 'I would like two ___ (book), please.',
      answers: ['books'],
      explanation: 'Book is countable: it exists in a singular and a plural form.',
    },
    {
      kind: 'multiple-choice',
      prompt: 'Money, information, and feedback are all…',
      options: ['uncountable nouns', 'countable nouns', 'plural nouns'],
      correctIndex: 0,
      explanation: 'They look countable, but they aren’t — be careful with these!',
    },
  ],

  'quantifiers': [
    {
      kind: 'multiple-choice',
      prompt: 'There\'s ___ milk in the fridge if you want a coffee break.',
      options: ['some', 'any', 'a'],
      correctIndex: 0,
      explanation: 'SOME is used with uncountable nouns in positive sentences.',
    },
    {
      kind: 'multiple-choice',
      prompt: 'There aren\'t ___ seats left for the workshop — it sold out!',
      options: ['any', 'some', 'a'],
      correctIndex: 0,
      explanation: 'ANY is used in negative sentences, with countable or uncountable nouns.',
    },
    {
      kind: 'fill-blank',
      prompt: 'Is there ___ email address to write to? (a or an?)',
      answers: ['an'],
      explanation: 'AN is used before vowel sounds with singular countable nouns.',
    },
    {
      kind: 'multiple-choice',
      prompt: 'Joana has ___ of friends in her English group.',
      options: ['lots', 'much', 'a little'],
      correctIndex: 0,
      explanation: 'Lots of works with both countable and uncountable nouns.',
    },
    {
      kind: 'multiple-choice',
      prompt: 'I\'ll be back in ___ of minutes.',
      options: ['a couple', 'a little', 'a bit'],
      correctIndex: 0,
      explanation: 'A couple of is used only with countable nouns.',
    },
    {
      kind: 'fill-blank',
      prompt: 'Would you like a ___ wine? (used only with uncountable nouns)',
      answers: ['little'],
      explanation: 'A little, much, and a bit of go only with uncountable nouns.',
    },
    {
      kind: 'multiple-choice',
      prompt: 'Which quantifier works ONLY with countable nouns?',
      options: ['many', 'much', 'a bit of'],
      correctIndex: 0,
      explanation: 'Many, a few, several, and both are only for countable nouns.',
    },
  ],

  'how-many-vs-how-much': [
    {
      kind: 'multiple-choice',
      prompt: 'How ___ money is left in the travel budget?',
      options: ['much', 'many', 'more'],
      correctIndex: 0,
      explanation: 'Money is uncountable, so we ask with how much.',
    },
    {
      kind: 'multiple-choice',
      prompt: 'How ___ books have you read this year?',
      options: ['many', 'much', 'more'],
      correctIndex: 0,
      explanation: 'Books is countable and plural, so we ask with how many.',
    },
    {
      kind: 'fill-blank',
      prompt: 'How ___ sugar do you need for your tea?',
      answers: ['much'],
      explanation: 'Sugar is uncountable — always singular, so it takes how much.',
    },
    {
      kind: 'fill-blank',
      prompt: 'How ___ cups of sugar do you need to make the cake?',
      answers: ['many'],
      explanation: 'Cups are countable, so the question uses how many.',
    },
    {
      kind: 'multiple-choice',
      prompt: 'You want to know the price of a shirt. You ask:',
      options: [
        'How much does this shirt cost?',
        'How many does this shirt cost?',
        'How much costs this shirt?',
      ],
      correctIndex: 0,
      explanation: 'Prices always take how much, because the question implies money.',
    },
    {
      kind: 'multiple-choice',
      prompt: 'How ___ plates do we need for tonight\'s dinner with the girls?',
      options: ['many', 'much', 'more'],
      correctIndex: 0,
      explanation: 'Plates is a countable noun with a plural form: how many.',
    },
  ],

  'comparatives-superlatives': [
    {
      kind: 'multiple-choice',
      prompt: 'My English is ___ than it was last year.',
      options: ['better', 'more good', 'gooder'],
      correctIndex: 0,
      explanation: 'Good is irregular: good → better → best.',
    },
    {
      kind: 'multiple-choice',
      prompt: 'This is the ___ restaurant in the neighborhood.',
      options: ['most expensive', 'expensivest', 'more expensive'],
      correctIndex: 0,
      explanation: 'Long adjectives form the superlative with the most in front.',
    },
    {
      kind: 'fill-blank',
      prompt: 'This class is ___ (easy) than I expected.',
      answers: ['easier'],
      explanation: 'Short adjectives form the comparative by adding -er.',
    },
    {
      kind: 'fill-blank',
      prompt: 'big → ___ (comparative)',
      answers: ['bigger'],
      explanation: 'Big ends in consonant + vowel + consonant, so the final consonant doubles.',
    },
    {
      kind: 'multiple-choice',
      prompt: 'Speaking English in a group is not ___ scary as I thought.',
      options: ['as', 'more', 'most'],
      correctIndex: 0,
      explanation: 'As ... as shows equality; not as ... as shows inequality.',
    },
    {
      kind: 'matching',
      instruction: 'Match each adjective with its irregular comparative:',
      pairs: [
        { left: 'bad', right: 'worse' },
        { left: 'little', right: 'less' },
        { left: 'far', right: 'further' },
        { left: 'much', right: 'more' },
      ],
      explanation: 'These very common adjectives have completely irregular comparative forms.',
    },
  ],

  'correlative-comparatives': [
    {
      kind: 'multiple-choice',
      prompt: '___ I practice, the more confident I feel.',
      options: ['The more', 'More', 'The most'],
      correctIndex: 0,
      explanation: 'The structure is: The + comparative..., the + comparative...',
    },
    {
      kind: 'fill-blank',
      prompt: 'The more you speak English, the ___ (easy) it becomes.',
      answers: ['easier'],
      explanation: 'Both parts of a correlative comparative use comparative forms.',
    },
    {
      kind: 'multiple-choice',
      prompt: 'Which sentence shows an UNEXPECTED outcome?',
      options: [
        'The more we learn, the less we know.',
        'The more you read, the more vocabulary you learn.',
        'The more experience you have, the more opportunities you may have.',
      ],
      correctIndex: 0,
      explanation: 'Sometimes the second change is different from what we might expect.',
    },
    {
      kind: 'fill-blank',
      prompt: 'The higher your salary, the ___ financial freedom you may have. (comparative of much)',
      answers: ['more'],
      explanation: 'Two things change together: as one grows, so does the other.',
    },
    {
      kind: 'multiple-choice',
      prompt: 'Which sentence is correct?',
      options: [
        'The longer you wait, the harder it becomes to start.',
        'Longer you wait, harder it becomes to start.',
        'The longer you wait, the hardest it becomes to start.',
      ],
      correctIndex: 0,
      explanation: 'Both clauses need THE + a comparative, never a superlative.',
    },
  ],

  'adjectives-ed-ing': [
    {
      kind: 'multiple-choice',
      prompt: 'After the long meeting, Helena felt ___.',
      options: ['tired', 'tiring', 'tire'],
      correctIndex: 0,
      explanation: '-ED adjectives describe a feeling — how a person feels.',
    },
    {
      kind: 'multiple-choice',
      prompt: 'The documentary about women scientists was really ___.',
      options: ['inspiring', 'inspired', 'inspire'],
      correctIndex: 0,
      explanation: '-ING adjectives describe the characteristic of a thing or situation.',
    },
    {
      kind: 'multiple-choice',
      prompt: 'I am ___ — I don\'t understand this report at all.',
      options: ['confused', 'confusing', 'confuse'],
      correctIndex: 0,
      explanation: 'I am confused = I don’t understand; I am confusing = I cause confusion.',
    },
    {
      kind: 'fill-blank',
      prompt: 'The roller-coaster ride was frightening, so we were ___.',
      answers: ['frightened'],
      explanation: 'The situation is frightening (-ing); the people feel frightened (-ed).',
    },
    {
      kind: 'multiple-choice',
      prompt: 'Which sentence means “my boyfriend is a boring person”?',
      options: [
        'My boyfriend is boring.',
        'My boyfriend is bored.',
        'My boyfriend is bore.',
      ],
      correctIndex: 0,
      explanation: '-ING describes a permanent characteristic; -ED describes a temporary feeling.',
    },
    {
      kind: 'fill-blank',
      prompt: 'I was ___ (shock) by how shocking the accident was last night.',
      answers: ['shocked'],
      explanation: 'The person feels shocked (-ed); the accident itself is shocking (-ing).',
    },
  ],

  'attributive-adjectives': [
    {
      kind: 'multiple-choice',
      prompt: 'In her job interview, Tatiana described herself as ___ — she puts a lot of effort into her work.',
      options: ['hard-working', 'easy-going', 'arrogant'],
      correctIndex: 0,
      explanation: 'Hard-working = a person who puts a lot of effort into their work.',
    },
    {
      kind: 'multiple-choice',
      prompt: 'Someone who has a strong desire to do well or to be successful is ___.',
      options: ['ambitious', 'patient', 'dull'],
      correctIndex: 0,
      explanation: 'Ambitious describes a strong desire to succeed — a great interview word.',
    },
    {
      kind: 'matching',
      instruction: 'Match each adjective with its meaning:',
      pairs: [
        { left: 'reliable', right: 'can be depended on' },
        { left: 'sociable', right: 'friendly, enjoys company' },
        { left: 'generous', right: 'happy to give more than usual' },
        { left: 'honest', right: 'always tells the truth' },
      ],
      explanation: 'Attributive adjectives describe personality traits — useful in job interviews.',
    },
    {
      kind: 'fill-blank',
      prompt: 'A person who adapts quickly to new situations and doesn\'t mind change is ___.',
      answers: ['flexible'],
      explanation: 'Flexible = someone who adapts quickly to new situations.',
    },
    {
      kind: 'multiple-choice',
      prompt: 'Which adjective describes a NEGATIVE trait?',
      options: ['arrogant', 'confident', 'thoughtful'],
      correctIndex: 0,
      explanation: 'Arrogant = someone who thinks they are better than other people.',
    },
    {
      kind: 'fill-blank',
      prompt: 'Someone who is self-assured and able to succeed is ___.',
      answers: ['confident'],
      explanation: 'Confident = self-assured and able to succeed.',
    },
  ],

  'adverbs-of-degree': [
    {
      kind: 'multiple-choice',
      prompt: 'After launching her startup, Marta was ___ exhausted — at the absolute limit!',
      options: ['totally', 'slightly', 'fairly'],
      correctIndex: 0,
      explanation: 'Totally, completely, and absolutely are absolute (++++) intensifiers.',
    },
    {
      kind: 'multiple-choice',
      prompt: 'The new report is ___ different from mine — just a small difference.',
      options: ['slightly', 'extremely', 'utterly'],
      correctIndex: 0,
      explanation: 'A little, a bit, and slightly are mild (+) intensifiers.',
    },
    {
      kind: 'fill-blank',
      prompt: 'I am ___ (slight) pushy in my decisions — just a little.',
      answers: ['slightly'],
      explanation: 'Slightly is a mild adverb of degree — it softens the adjective.',
    },
    {
      kind: 'matching',
      instruction: 'Match each intensity level with an adverb of degree:',
      pairs: [
        { left: 'Mild (+)', right: 'a bit' },
        { left: 'Medium (++)', right: 'fairly' },
        { left: 'Strong (+++)', right: 'extremely' },
        { left: 'Absolute (++++)', right: 'completely' },
      ],
      explanation: 'Adverbs of degree let you say exactly HOW happy, busy, or tired you are.',
    },
    {
      kind: 'multiple-choice',
      prompt: 'Which adverb of degree is the STRONGEST?',
      options: ['absolutely', 'quite', 'a little'],
      correctIndex: 0,
      explanation: 'Absolute adverbs like absolutely and utterly are at the top of the scale.',
    },
    {
      kind: 'fill-blank',
      prompt: 'This topic is ___ interesting. (medium intensity, starts with qu-)',
      answers: ['quite'],
      explanation: 'Fairly, pretty, quite, and rather are medium (++) intensifiers.',
    },
  ],

  'phrasal-verbs-general': [
    {
      kind: 'multiple-choice',
      prompt: 'I knew she was going to ___ the budget results at the meeting.',
      options: ['bring up', 'put off', 'get by'],
      correctIndex: 0,
      explanation: 'To bring up = to introduce a topic.',
    },
    {
      kind: 'multiple-choice',
      prompt: 'I haven\'t done my taxes yet — I\'ve been ___ it ___ for weeks.',
      options: ['putting / off', 'getting / over', 'ruling / out'],
      correctIndex: 0,
      explanation: 'To put (something) off = to postpone.',
    },
    {
      kind: 'fill-blank',
      prompt: 'The printer is running ___ of toner again.',
      answers: ['out'],
      explanation: 'To run out of = to have a decreasing or zero amount of something.',
    },
    {
      kind: 'matching',
      instruction: 'Match each phrasal verb with its meaning:',
      pairs: [
        { left: 'put up with', right: 'tolerate' },
        { left: 'come up with', right: 'have an idea' },
        { left: 'find out', right: 'gain knowledge about something' },
        { left: 'look after', right: 'take care of' },
        { left: 'get rid of', right: 'discard, throw away' },
      ],
      explanation: 'A phrasal verb is a verb + a particle, and together they form one meaning.',
    },
    {
      kind: 'multiple-choice',
      prompt: 'I\'m really looking ___ to the conference next week.',
      options: ['forward', 'after', 'out'],
      correctIndex: 0,
      explanation: 'To look forward to = to be excited about something in the future.',
    },
    {
      kind: 'fill-blank',
      prompt: 'I can get ___ in English, but my French is a little rusty. (= to survive, “se virar”)',
      answers: ['by'],
      explanation: 'To get by = to survive, “se virar”.',
    },
  ],

  'phrasal-verbs-work': [
    {
      kind: 'multiple-choice',
      prompt: 'I\'ll ___ with you next week to discuss our potential collaboration.',
      options: ['follow up', 'step down', 'hand out'],
      correctIndex: 0,
      explanation: 'Follow up = to continue communication after an initial meeting or conversation.',
    },
    {
      kind: 'multiple-choice',
      prompt: 'I had to ___ the invitation because of a prior commitment.',
      options: ['turn down', 'take on', 'back up'],
      correctIndex: 0,
      explanation: 'Turn down = to refuse or decline an offer or invitation.',
    },
    {
      kind: 'fill-blank',
      prompt: 'Feel free to reach ___ if you have any questions about the project.',
      answers: ['out'],
      explanation: 'Reach out = to initiate contact with someone.',
    },
    {
      kind: 'matching',
      instruction: 'Match each work phrasal verb with its meaning:',
      pairs: [
        { left: 'set up', right: 'arrange a meeting or event' },
        { left: 'take on', right: 'accept a task or responsibility' },
        { left: 'step down', right: 'resign from a position' },
        { left: 'touch base', right: 'make brief contact to check in' },
        { left: 'go over', right: 'review something carefully' },
      ],
      explanation: 'These phrasal verbs appear all the time in meetings and work emails.',
    },
    {
      kind: 'multiple-choice',
      prompt: 'She ___ as very confident and knowledgeable in the interview.',
      options: ['comes across', 'runs into', 'catches up'],
      correctIndex: 0,
      explanation: 'Come across = to seem or appear in a certain way to others.',
    },
    {
      kind: 'fill-blank',
      prompt: 'Thank you for ___ (back) me up during the presentation!',
      answers: ['backing'],
      explanation: 'Back up = to support someone or something.',
    },
  ],
};
